import { isEmpty } from 'lodash'
import { ChangeEvent, KeyboardEvent, ReactElement, ReactNode, useState } from 'react'
import styled from 'styled-components'

import { Input } from 'components/Input'
import { Label, OptionalLabel } from 'components/v2/Label'
import { Tag } from 'components/v2/Tag'
import { KEY_CODES } from 'utils/string'
import { FormattedMessage } from 'react-intl'

const Wrapper = styled.div<{ showError?: boolean; hasValues?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  width: auto;
  border-radius: 3px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ showError }): string => (showError ? '#FF627B' : '#E3E3E3')};
  padding: 15px 16px;
  cursor: text;
  background-color: #ffffff;
  gap: 6px;

  &:hover,
  &:focus-within {
    border: 1px solid #717171;
  }
`

const StyledInput = styled(Input)`
  border: none;
  max-width: 100%;
  min-width: 40px;

  ${(props): string | undefined => {
    if (props.placeholder && !props.value) {
      return `
        width: ${props.placeholder.length * 10 + 2}px;
        max-width: 100%;
      `
    }
  }}
`

interface IInputTagsProps {
  values: string[]
  onChange: (values: string[]) => void
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void
  id?: string
  className?: string
  placeholder: string
  label?: ReactNode
  validate?: (value: string) => void
  isValid?: (value: string) => boolean
  optional?: boolean
}

const InputTags = ({
  values = [],
  onChange,
  onBlur,
  id,
  className,
  placeholder,
  label,
  validate,
  isValid,
  optional
}: IInputTagsProps): ReactElement => {
  const [value, setValue] = useState<string>('')

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setValue(value)

    if (validate) {
      validate(value)
    }
  }

  const handleSelectValue = (value: string): void => {
    if (!isEmpty(value)) {
      onChange([...values, value])

      if (isValid && !isValid(value)) {
        return
      }

      setValue('')
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.keyCode) {
      case KEY_CODES.ENTER:
        if (value) {
          event.preventDefault()
          event.stopPropagation()
          handleSelectValue(value)
        }
        break
      case KEY_CODES.BACKSPACE:
        if (isEmpty(value)) {
          const newValues = values.slice(0, values.length - 1)
          onChange(newValues)
        }
    }
  }

  const handleTagDelete = (tag: string): void => {
    const newValues = values.filter((value: string) => {
      return value !== tag
    })
    onChange(newValues)
  }

  const handleBlur = (event: ChangeEvent<HTMLInputElement>): void => {
    handleSelectValue(value)

    if (onBlur) {
      onBlur(event)
    }
  }

  return (
    <label>
      {label ? (
        <Label>
          {label}{' '}
          {optional && (
            <OptionalLabel>
              <FormattedMessage id="form.optional.label" defaultMessage="(Optional)" />
            </OptionalLabel>
          )}
        </Label>
      ) : null}
      <Wrapper hasValues={values.length > 0} className={className}>
        {values.map((tag: string, index: number) => {
          return (
            <Tag key={index} onDelete={handleTagDelete.bind(this, tag)}>
              {tag}
            </Tag>
          )
        })}
        <StyledInput
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          noBorder
          noPadding
          hasDynamicWidth
        />
      </Wrapper>
    </label>
  )
}

export { InputTags }
